import { useTranslation } from 'react-i18next'

import { Box, Stack, Typography, useMediaQuery, useTheme } from '@mui/material'

import CheckIcon from '@mui/icons-material/Check'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import CloseIcon from '@mui/icons-material/Close'

import type { IFeature, PlanName } from '@components/StandardPlans'

import type { ISelectableInterval } from '@utils/plan-detail'

import {
  cellCheckCircleIconSx,
  cellCheckIconSx,
  cellCloseIconSx,
  featureSx,
  planItemSx,
  planStackSx,
} from './styles'

import { PlanPricing } from '../PlanPricing'

interface IPlanItemProps {
  alias: string
  name: PlanName
  features: IFeature[]
  paymentPeriods?: ISelectableInterval[]
  isLoading: boolean
  isAvailable?: boolean
  isHighlighted?: boolean
  isCustomPlan: boolean
  hasPromotionCode?: boolean
  mobileView: boolean
  planSetWidth: number
  onClick?: () => void
}

const PlanItem = ({
  alias,
  name,
  features,
  paymentPeriods,
  isLoading,
  isAvailable,
  isHighlighted,
  isCustomPlan,
  hasPromotionCode,
  mobileView,
  planSetWidth,
  onClick,
  ...rest
}: IPlanItemProps) => {
  const { t } = useTranslation()
  const theme = useTheme()

  const buildCellIcon = ({
    value,
    isHighlighted = false,
  }: {
    value: string | boolean
    isHighlighted?: boolean
  }) => {
    if (!value) {
      return <CloseIcon sx={cellCloseIconSx} />
    }

    if (isHighlighted) {
      return <CheckCircleOutlineIcon sx={cellCheckCircleIconSx} />
    }

    return <CheckIcon sx={cellCheckIconSx} />
  }

  const isDesktop = useMediaQuery(theme.breakpoints.between(planSetWidth, 1920))

  return (
    <Box
      sx={planItemSx({ isDesktop, mobileView })}
      data-testid="plan-item"
      {...rest}
    >
      <PlanPricing
        alias={alias || name}
        paymentPeriods={paymentPeriods}
        isAvailable={isAvailable}
        isHighlighted={isHighlighted}
        isLoading={isLoading}
        isCustomPlan={isCustomPlan}
        hasPromotionCode={hasPromotionCode}
        planSetWidth={planSetWidth}
        onClick={onClick}
      />
      <Stack sx={planStackSx}>
        {features.map((featureByPlanName) => (
          <Stack
            data-testid="feature-item"
            direction="row"
            sx={featureSx}
            key={`${name}-${featureByPlanName.name}`}
          >
            {buildCellIcon({
              value: featureByPlanName[name],
              isHighlighted,
            })}
            <Typography>{`${t(featureByPlanName.name)} ${
              typeof featureByPlanName[name] === 'string'
                ? featureByPlanName[name]
                : ''
            }`}</Typography>
          </Stack>
        ))}
      </Stack>
    </Box>
  )
}

export { PlanItem }
